import { useSelector, useDispatch } from 'react-redux';
import Chart from 'react-apexcharts';
import { ethers } from 'ethers';
import { useEffect, useMemo } from 'react';

import { options, series } from './Charts.config';
import { chartSelector } from '../store/selectors';
import Loading from './Loading';
import { loadAllSwaps } from '../store/interactions';
import { getNetworkConfig } from '../utils/network';
import { formatRate, shortenAddress, shortenHash } from '../utils/format';
import AMM_ABI from '../abis/AMM.json';

const Charts = () => {
  const provider = useSelector((state) => state.provider.connection);
  const chainId = useSelector((state) => state.provider.chainId);
  const tokens = useSelector((state) => state.tokens.contracts);
  const symbols = useSelector((state) => state.tokens.symbols);
  const chart = useSelector(chartSelector);
  const dispatch = useDispatch();

  const networkConfig = useMemo(() => getNetworkConfig(chainId), [chainId]);
  const amm = useMemo(() => {
    if (!provider || !networkConfig) return null;
    return new ethers.Contract(networkConfig.amm1.ammAddress, AMM_ABI, provider);
  }, [provider, networkConfig]);

  useEffect(() => {
    if (provider && amm) {
      loadAllSwaps(provider, amm, dispatch);
    }
  }, [provider, amm, dispatch]);

  const dappAddress = tokens?.dappToken1Address;
  const chartSeries = chart?.series?.length ? chart.series : series;

  return (
    <div className="charts-layout" style={{ maxWidth: 'var(--content-wide)', margin: '0 auto' }}>
      {provider && amm ? (
        <>
          <section className="card card--elevated" aria-labelledby="charts-title">
            <p className="card__eyebrow">AMM 1 activity</p>
            <h1 id="charts-title" className="card__title">
              Charts
            </h1>
            <p className="card__subtitle">
              On-chain swap events for AMM 1 only. No synthetic volume, TVL, or APY.
            </p>
            <div style={{ minHeight: 320 }}>
              <Chart
                type="line"
                options={options}
                series={chartSeries}
                width="100%"
                height={320}
              />
            </div>
          </section>

          <section className="card" aria-labelledby="swaps-title">
            <h2 id="swaps-title" className="card__title" style={{ fontSize: '1.1rem' }}>
              Recent swaps
            </h2>
            <div className="charts-table-wrap">
              {chart?.swaps?.length ? (
                <table className="charts-table">
                  <thead>
                    <tr>
                      <th scope="col">Tx</th>
                      <th scope="col">Token In</th>
                      <th scope="col">Amount In</th>
                      <th scope="col">Token Out</th>
                      <th scope="col">Amount Out</th>
                      <th scope="col">Trader</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chart.swaps.map((swap, index) => (
                      <tr key={swap.hash || index}>
                        <td title={swap.hash}>{shortenHash(swap.hash)}</td>
                        <td>
                          {swap.args.tokenIn === dappAddress
                            ? symbols?.dappToken1Symbol || 'DAPP'
                            : symbols?.usdToken1Symbol || 'USD'}
                        </td>
                        <td title={ethers.utils.formatUnits(swap.args.amountIn.toString(), 'ether')}>
                          {formatRate(
                            ethers.utils.formatUnits(swap.args.amountIn.toString(), 'ether'),
                            4
                          )}
                        </td>
                        <td>
                          {swap.args.tokenOut === dappAddress
                            ? symbols?.dappToken1Symbol || 'DAPP'
                            : symbols?.usdToken1Symbol || 'USD'}
                        </td>
                        <td
                          title={ethers.utils.formatUnits(swap.args.amountOut.toString(), 'ether')}
                        >
                          {formatRate(
                            ethers.utils.formatUnits(swap.args.amountOut.toString(), 'ether'),
                            4
                          )}
                        </td>
                        <td title={swap.args.trader}>{shortenAddress(swap.args.trader)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="empty-note">No swap events loaded for this AMM yet.</p>
              )}
            </div>
          </section>
        </>
      ) : (
        <Loading />
      )}
    </div>
  );
};

export default Charts;
